import { useEffect, useMemo, useRef, useState } from 'react';
import { CHRONICLE_LOOK } from '@continuum/show-core';
import type { CSSProperties, PointerEvent, RefObject } from 'react';
import { drawChronicleCard } from '../ui/chronicleCard.js';
import type { ChronicleCardData } from '../ui/chronicleCard.js';
import { wrappedCards } from '../ui/wrappedCards.js';
import type { WrappedInput } from '../ui/wrappedCards.js';
import { SCORE_COPY } from '../ui/scoreText.js';
import { WRAPPED_COPY } from '../ui/wrappedText.js';
import { storyBars, storyHidden, storyHold, storyStart, storyStep, storyTick, storyTouch } from '../ui/storyClock.js';
import {
  VideoStopped,
  keepAwake,
  loadImage,
  recordVideo,
  saveRoute,
  videoFile,
  videoPlan,
  videoSupported,
  videoTypes,
} from '../ui/wrappedVideo.js';
import type { Loaded } from '../ui/wrappedVideo.js';
import { mono, sans } from '../ui/atoms.js';
import { BottomSheet } from '../ui/BottomSheet.js';

interface Drawn {
  id: string;
  file: File;
  url: string;
}

type VideoState =
  | { kind: 'off' }
  | { kind: 'making'; left: number; done: number }
  | { kind: 'ready'; file: File; url: string }
  | { kind: 'failed'; why: 'hidden' | 'failed' };

/** Why a recording was stopped from outside: the STOP button, the screen going away, or the sheet closing. */
type StopWhy = 'stop' | 'hidden' | 'closed';

const GOLD = CHRONICLE_LOOK.acc;

/**
 * The evening, wrapped, on the guest's own phone at the farewell, played like an Instagram story (Marek,
 * 2026-10-10): each slide five seconds under its own bar, a tap on the left edge back and anywhere else on,
 * a finger held on the screen to pause. It stops on the last slide; pull down or CLOSE to put it away.
 * The chronicle card is the last slide, the one to keep.
 *
 * SAVE THE VIDEO plays the story cards into a silent video, five seconds a slide, recorded on the phone
 * while it plays (ui/wrappedVideo.ts); then one tap saves it: the share sheet on an iPhone (its Save Video
 * puts it in Photos), a download elsewhere. The share sheet needs a tap of its own, so the recording
 * finishing never opens it. Every slide still saves as a picture.
 */
export function Wrapped({
  input,
  chronicle,
  onClose,
}: {
  input: WrappedInput;
  chronicle: ChronicleCardData;
  onClose: () => void;
}) {
  const s = SCORE_COPY[input.lang];
  const w = WRAPPED_COPY[input.lang];
  const [cards, setCards] = useState<Drawn[]>([]);
  const [total, setTotal] = useState(0);
  const [at, setAt] = useState(0);
  const [note, setNote] = useState<'saved' | 'failed' | null>(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'story' | 'video'>('story');
  const [video, setVideo] = useState<VideoState>({ kind: 'off' });
  const clock = useRef(storyStart());
  const bars = useRef<(HTMLSpanElement | null)[]>([]);
  const touch = useRef<{ x: number; y: number; at: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const run = useRef<{ ctrl: AbortController; why: StopWhy | null } | null>(null);
  const shown = useRef({ left: -1, done: -1 });
  const canFilm = useMemo(() => videoSupported(), []);
  const route = useMemo(() => {
    let canShareFile = false;
    try {
      canShareFile = navigator.canShare?.({ files: [new File([''], 'probe.mp4', { type: 'video/mp4' })] }) === true;
    } catch {
      canShareFile = false;
    }
    return saveRoute({
      userAgent: navigator.userAgent,
      touchPoints: navigator.maxTouchPoints ?? 0,
      canShareFile,
      canDownload: 'download' in HTMLAnchorElement.prototype,
    });
  }, []);
  const base = `ss-aurora-${(input.summary.callsign ?? 'crew').toLowerCase()}`;

  // Draw every card once, one after another, so the first plays while the rest are still coming.
  useEffect(() => {
    let gone = false;
    const made: Drawn[] = [];
    const specs = wrappedCards(input);
    const pages = specs.length + 1;
    setTotal(pages);
    void (async () => {
      const steps = [
        ...specs.map((spec, i) => ({ id: spec.id, draw: () => spec.draw(i + 1, pages) })),
        { id: 'chronicle', draw: () => drawChronicleCard(chronicle) },
      ];
      for (const [i, step] of steps.entries()) {
        try {
          const blob = await step.draw();
          if (gone) return;
          const file = new File([blob], `${base}-${i + 1}-${step.id}.png`, { type: 'image/png' });
          made.push({ id: step.id, file, url: URL.createObjectURL(blob) });
          setCards([...made]);
        } catch {
          // A card this phone cannot draw is left out; the rest still come.
          setTotal((n) => Math.max(made.length, n - 1));
        }
      }
    })();
    return () => {
      gone = true;
      for (const card of made) URL.revokeObjectURL(card.url);
    };
    // Drawn once per opening: the evening is over, and nothing on these cards will change.
  }, []);

  // The story plays by itself: the clock runs every frame and fills the bars, and React hears only of a
  // new slide. It stands still while a finger is held, the page is hidden, the share sheet is up, or the
  // video is being made.
  const ready = cards.length;
  const playing = view === 'story' && !busy;
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    let last = performance.now();
    const frame = (now: number) => {
      // A frame late after the page was away is not time played: at most a tenth of a second at once.
      const next = storyTick(clock.current, Math.min(100, now - last), ready);
      last = now;
      clock.current = next;
      paintBars();
      if (next.index !== at) setAt(next.index);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [playing, ready, at]);

  useEffect(() => {
    const away = () => {
      clock.current = storyHidden(clock.current, document.visibilityState === 'hidden');
    };
    // The Wrapped opens by itself at the clicker's last NEXT, perhaps on a phone in a pocket: hidden from the start.
    away();
    document.addEventListener('visibilitychange', away);
    return () => document.removeEventListener('visibilitychange', away);
  }, []);

  const paintBars = () => {
    const fills = storyBars(clock.current, total);
    fills.forEach((f, i) => {
      const el = bars.current[i];
      if (el) el.style.transform = `scaleX(${f})`;
    });
  };
  // A new slide count, or back from the video: the bars as the clock has them.
  useEffect(paintBars, [total, view, at]);

  const step = (dir: 1 | -1) => {
    setNote(null);
    clock.current = storyStep(clock.current, dir, ready);
    setAt(clock.current.index);
    paintBars();
  };

  const down = (e: PointerEvent<HTMLDivElement>) => {
    touch.current = { x: e.clientX, y: e.clientY, at: performance.now() };
    clock.current = storyHold(clock.current, true);
  };
  const up = (e: PointerEvent<HTMLDivElement>) => {
    const from = touch.current;
    touch.current = null;
    clock.current = storyHold(clock.current, false);
    if (!from) return;
    const box = e.currentTarget.getBoundingClientRect();
    const said = storyTouch({
      x: e.clientX - box.left,
      width: box.width,
      dx: e.clientX - from.x,
      dy: e.clientY - from.y,
      ms: performance.now() - from.at,
    });
    if (said !== 'none') step(said === 'on' ? 1 : -1);
  };
  const cancel = () => {
    touch.current = null;
    clock.current = storyHold(clock.current, false);
  };

  // ------------------------------------------------------------------ the video

  // A finished video's address lives as long as the video does.
  useEffect(() => {
    if (video.kind !== 'ready') return;
    const url = video.url;
    return () => URL.revokeObjectURL(url);
  }, [video]);

  // Closing the sheet stops a recording under way.
  useEffect(
    () => () => {
      const r = run.current;
      if (r) {
        r.why = 'closed';
        r.ctrl.abort();
      }
    },
    [],
  );

  const stopVideo = (why: StopWhy) => {
    const r = run.current;
    if (!r || r.why) return;
    r.why = why;
    r.ctrl.abort();
  };

  // A hidden page stops drawing, and the video would come out frozen: stop cleanly, and offer it again.
  const making = video.kind === 'making';
  useEffect(() => {
    if (!making) return;
    const away = () => {
      if (document.visibilityState === 'hidden') stopVideo('hidden');
    };
    const gone = () => stopVideo('hidden');
    document.addEventListener('visibilitychange', away);
    window.addEventListener('pagehide', gone);
    return () => {
      document.removeEventListener('visibilitychange', away);
      window.removeEventListener('pagehide', gone);
    };
  }, [making]);

  const storyFiles = cards.filter((c) => c.id !== 'chronicle').map((c) => c.file);
  const allDrawn = total > 0 && cards.length >= total;
  const seconds = Math.round(videoPlan(Math.max(1, total - 1)).total);

  const makeVideo = async () => {
    if (run.current || !allDrawn || storyFiles.length === 0) return;
    const me: { ctrl: AbortController; why: StopWhy | null } = { ctrl: new AbortController(), why: null };
    run.current = me;
    const plan = videoPlan(storyFiles.length);
    shown.current = { left: -1, done: -1 };
    setNote(null);
    setView('video');
    setVideo({ kind: 'making', left: Math.ceil(plan.total), done: 0 });
    const awake = await keepAwake();
    let images: Loaded[] = [];
    try {
      const canvas = await onPage(canvasRef, me.ctrl.signal);
      images = await Promise.all(storyFiles.map((file) => loadImage(file)));
      const result = await recordVideo({
        canvas,
        images: images.map((image) => image.source),
        mimeTypes: videoTypes(window.MediaRecorder),
        signal: me.ctrl.signal,
        onTick: (elapsed, length) => {
          // Only what the guest can see change: the seconds left, and the bar in fiftieths.
          const left = Math.max(0, Math.ceil(length - elapsed));
          const done = Math.round((elapsed / length) * 50) / 50;
          if (left === shown.current.left && done === shown.current.done) return;
          shown.current = { left, done };
          setVideo((v) => (v.kind === 'making' ? { kind: 'making', left, done } : v));
        },
      });
      if (me.why) throw new VideoStopped('aborted');
      const file = videoFile(result.blob, result.mimeType, base);
      setVideo({ kind: 'ready', file, url: URL.createObjectURL(file) });
    } catch {
      if (me.why === 'closed') return;
      if (me.why === 'stop') {
        setVideo({ kind: 'off' });
        setView('story');
        return;
      }
      setVideo({ kind: 'failed', why: me.why === 'hidden' ? 'hidden' : 'failed' });
    } finally {
      for (const image of images) image.close();
      awake.release();
      if (run.current === me) run.current = null;
    }
  };

  // ------------------------------------------------------------------ keeping things

  const download = async (files: File[]) => {
    for (const [i, file] of files.entries()) {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      // Browsers refuse a burst of downloads; a short pause between them is let through.
      if (i < files.length - 1) await new Promise((r) => setTimeout(r, 400));
    }
  };

  /** Pictures: the share sheet where the phone has one (Save Image, or straight to a story), a download otherwise. */
  const keep = async (files: File[], how: 'share' | 'download' | 'either' = 'either') => {
    setBusy(true);
    setNote(null);
    try {
      if (how !== 'download' && navigator.canShare?.({ files })) await navigator.share({ files });
      else await download(files);
      setNote('saved');
    } catch (err) {
      // Closing the share sheet is a choice, not a failure.
      setNote(err instanceof DOMException && err.name === 'AbortError' ? null : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const saveVideo = (file: File) => void keep([file], route);

  // ------------------------------------------------------------------ the look

  const button: CSSProperties = {
    minHeight: 50,
    borderRadius: 12,
    fontFamily: sans,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 2,
    cursor: 'pointer',
  };
  const primary = (on: boolean): CSSProperties => ({ ...button, border: 'none', background: GOLD, color: '#17130a', opacity: on ? 1 : 0.55 });
  const secondary = (on: boolean): CSSProperties => ({
    ...button,
    border: `1px solid ${GOLD}88`,
    background: 'transparent',
    color: GOLD,
    opacity: on ? 1 : 0.55,
  });
  /** Pinned inside its box, so a tall card or video shrinks to fit rather than pushing the buttons off. */
  const fitted: CSSProperties = {
    position: 'absolute',
    inset: 0,
    margin: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: 12,
    boxShadow: '0 0 40px rgba(0,0,0,.6)',
    pointerEvents: 'none',
  };
  const sub: CSSProperties = { fontFamily: mono, fontSize: 11, fontWeight: 500, letterSpacing: 0.4, opacity: 0.8 };
  const twoLines: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 };
  const hint = route === 'share' ? w.videoSaveHint : w.videoDownloadHint;

  const current = cards[Math.min(at, cards.length - 1)];
  const noteText =
    note === 'failed'
      ? s.wrappedFailed
      : note === 'saved'
        ? s.wrappedSaved
        : view === 'story'
          ? w.storyHint
          : video.kind === 'making'
            ? w.videoMaking(video.left)
            : video.kind === 'ready'
              ? w.videoReady
              : '';
  const noteColor = note === 'failed' ? '#ff9f8f' : view === 'video' && video.kind === 'making' ? GOLD : '#b3a37c';

  const header =
    view === 'story' ? (
      // One bar a slide, filling as it plays (the clock paints them, not React).
      <div style={{ display: 'flex', gap: 4, paddingBottom: 8 }}>
        {Array.from({ length: Math.max(1, total) }, (_, i) => (
          <span key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,.18)', overflow: 'hidden' }}>
            <span
              ref={(el) => {
                bars.current[i] = el;
              }}
              style={{ display: 'block', height: '100%', background: GOLD, transform: 'scaleX(0)', transformOrigin: 'left center' }}
            />
          </span>
        ))}
      </div>
    ) : (
      <div style={{ paddingBottom: 8 }}>
        <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,.16)', overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${video.kind === 'making' ? video.done * 100 : video.kind === 'ready' ? 100 : 0}%`,
              background: GOLD,
              transition: 'width .25s linear',
            }}
          />
        </div>
      </div>
    );

  const footer =
    view === 'story' ? (
      <>
        <Note text={noteText} color={noteColor} />
        {canFilm && (
          <button
            disabled={!allDrawn || busy}
            onClick={() => (video.kind === 'ready' ? saveVideo(video.file) : void makeVideo())}
            style={{ ...primary(allDrawn && !busy), ...twoLines }}
          >
            <span>▶ {w.videoMake}</span>
            <span style={sub}>{video.kind === 'ready' ? hint : w.videoMakeSub(seconds)}</span>
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            disabled={!current || busy}
            onClick={() => current && void keep([current.file])}
            style={{ ...(canFilm ? secondary(Boolean(current) && !busy) : primary(Boolean(current) && !busy)), flex: 1.6 }}
          >
            {s.wrappedSave}
          </button>
          <button disabled={!allDrawn || busy} onClick={() => void keep(cards.map((c) => c.file))} style={{ ...secondary(allDrawn && !busy), flex: 1 }}>
            {s.wrappedSaveAll}
          </button>
        </div>
      </>
    ) : video.kind === 'making' ? (
      <>
        <Note text={noteText} color={noteColor} />
        <button onClick={() => stopVideo('stop')} style={secondary(true)}>
          {w.videoStop}
        </button>
      </>
    ) : video.kind === 'ready' ? (
      <>
        <Note text={noteText} color={noteColor} />
        <button disabled={busy} onClick={() => saveVideo(video.file)} style={{ ...primary(!busy), ...twoLines }}>
          <span>{w.videoSave}</span>
          <span style={sub}>{hint}</span>
        </button>
        <button
          onClick={() => {
            setNote(null);
            setView('story');
          }}
          style={secondary(true)}
        >
          {w.videoBack}
        </button>
      </>
    ) : (
      <>
        <Note text={noteText} color={noteColor} />
        <button disabled={!allDrawn} onClick={() => void makeVideo()} style={primary(allDrawn)}>
          {w.videoAgain}
        </button>
        <button
          onClick={() => {
            setVideo({ kind: 'off' });
            setView('story');
          }}
          style={secondary(true)}
        >
          {w.videoBack}
        </button>
      </>
    );

  return (
    <BottomSheet
      id="wrapped"
      size="full"
      zIndex={62}
      onDismiss={onClose}
      label={s.wrappedOpen}
      closeLabel={s.wrappedClose}
      tone={{ bg: '#06060a', fg: '#f3ead6', border: 'rgba(217,185,106,.4)', grip: GOLD }}
      bodyStyle={{ padding: '0 14px 8px', gap: 10 }}
      header={header}
      footer={footer}
    >
      {view === 'story' ? (
        // The story's touch area: left edge back, the rest on, a held finger pauses. The buttons are in the
        // footer, outside it, so a tap on them is never a tap on an edge.
        <div
          onPointerDown={down}
          onPointerUp={up}
          onPointerCancel={cancel}
          onContextMenu={(e) => e.preventDefault()}
          style={{ position: 'relative', flex: '1 1 auto', minHeight: 200, touchAction: 'pan-y', userSelect: 'none' }}
        >
          {current ? <img src={current.url} alt="" draggable={false} style={fitted} /> : <Centred>{s.wrappedMaking}</Centred>}
        </div>
      ) : (
        <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 200, userSelect: 'none' }}>
          {video.kind === 'making' && (
            // The video as it is made: the canvas the recorder films, on the page so every phone films it.
            <canvas ref={canvasRef} width={1080} height={1920} style={fitted} />
          )}
          {video.kind === 'ready' && (
            <video
              key={video.url}
              ref={(el) => {
                if (!el) return;
                // Set here, not as a prop: a phone plays a video by itself only if it is muted from the start.
                el.muted = true;
                el.defaultMuted = true;
                // A phone that will not play it by itself (low power) shows the first slide, not the dark fade-in.
                void el.play().catch(() => {
                  el.currentTime = 1.5;
                });
              }}
              src={video.url}
              autoPlay
              loop
              playsInline
              disablePictureInPicture
              controls={false}
              style={{ ...fitted, background: '#06060a' }}
            />
          )}
          {video.kind === 'failed' && <Centred color="#f3ead6">{video.why === 'hidden' ? w.videoHidden : w.videoFailed}</Centred>}
        </div>
      )}
      {view === 'video' && video.kind === 'making' && (
        <div style={{ flex: 'none', fontFamily: mono, fontSize: 11, textAlign: 'center', color: '#8f84a6' }}>{w.videoStay}</div>
      )}
    </BottomSheet>
  );
}

function Note({ text, color }: { text: string; color: string }) {
  return <div style={{ minHeight: 16, fontFamily: mono, fontSize: 12, textAlign: 'center', color }}>{text}</div>;
}

function Centred({ children, color = GOLD }: { children: string; color?: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        padding: '0 12px',
        fontFamily: mono,
        fontSize: 13,
        lineHeight: 1.5,
        letterSpacing: 1,
        textAlign: 'center',
        color,
      }}
    >
      {children}
    </div>
  );
}

/** The recording canvas, once React has put it on the page. */
async function onPage(ref: RefObject<HTMLCanvasElement | null>, signal: AbortSignal): Promise<HTMLCanvasElement> {
  for (let i = 0; i < 60; i += 1) {
    if (signal.aborted) throw new VideoStopped('aborted');
    if (ref.current) return ref.current;
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));
  }
  throw new VideoStopped('failed');
}
